import styled from '@emotion/styled';
import {
  palette,
  gradient,
  radius,
  spacing,
  shadow,
  transition,
  duration,
  easing,
  layout,
  zIndex,
} from '../../style-utils/theme';
import { font } from '../../style-utils/text';
import { m } from '../../motion';

export const ProfileButton = styled.button<{
  dashboard?: boolean;
}>`
  box-sizing: border-box;
  width: ${layout.touchTarget};
  height: ${layout.touchTarget};
  min-width: ${layout.touchTarget};
  min-height: ${layout.touchTarget};
  /* Never let a flex row squeeze the avatar on one axis — that is what turns
     the circle into an egg. */
  flex-shrink: 0;
  ${({ dashboard }) =>
    dashboard ? `margin-bottom: 0px;` : `margin-bottom: ${spacing.md};`}
  border-radius: ${radius.circle};
  /* Brand-tinted avatar — a small premium concierge badge */
  background: ${gradient.primary};
  color: ${palette.white};
  box-shadow: ${shadow.sm};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  user-select: none;
  padding: 0;
  border: none;
  transition:
    ${transition.colors},
    transform ${duration.fast} ${easing.spring};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${shadow.brand};
  }
  &:active {
    transform: scale(0.97);
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const ProfileModal = m.div<{
  dashboard?: boolean;
  $top?: number;
  $left?: number;
  $right?: number;
  $bottom?: number;
}>`
  /* FIXED, not absolute, and portalled to <body> by Profile.
     An absolutely-positioned popover resolves against the nearest positioned
     ancestor and its z-index competes only INSIDE that ancestor's stacking
     context. The dashboard header establishes one, so this panel painted
     under the Export control no matter how large its z-index was. See
     components/modal/modal-portal.tsx for the full account of that trap.
     Coordinates come from the trigger's getBoundingClientRect(), so the panel
     still hangs off the avatar rather than off a hardcoded header height.
     NO BACKTICKS IN THIS COMMENT: it is inside a styled template literal. */
  position: fixed;
  width: fit-content;
  min-width: 200px;
  /* dvw, not vw. On desktop, vw counts the classic scrollbar's gutter, so a
     panel clamped to 100vw on a scrolled page is about 15px wider than the
     space it actually has and overhangs the right edge. The vw line stays
     first as the pre-Safari-15.4 fallback.
     NO BACKTICKS IN THIS COMMENT: it is inside a styled template literal. */
  max-width: calc(100vw - ${spacing.lg});
  max-width: calc(100dvw - ${spacing.lg});
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  ${({ $top }) => ($top !== undefined ? `top: ${$top}px;` : '')}
  ${({ $bottom }) => ($bottom !== undefined ? `bottom: ${$bottom}px;` : '')}
  ${({ $left }) => ($left !== undefined ? `left: ${$left}px;` : '')}
  ${({ $right }) => ($right !== undefined ? `right: ${$right}px;` : '')}
  transform-origin: ${({ dashboard }) => (dashboard ? 'top right' : 'bottom left')};
  box-shadow: ${shadow.lg};
  padding: ${spacing.md};
  gap: ${spacing.xs};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: ${zIndex.dropdown};
`;

export const ButtonContainer = styled.button<{ dashboard?: boolean }>`
  width: 100%;
  height: ${layout.buttonHeight};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  color: ${palette.ink};
  cursor: pointer;
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${shadow.xs};
  transition:
    ${transition.colors},
    transform ${duration.fast} ${easing.spring};

  &:hover {
    background: ${palette.irisTintSoft};
    border-color: ${palette.lagoon};
    color: ${palette.lagoon};
    transform: translateY(-1px);
    box-shadow: ${shadow.sm};
  }
  &:active {
    transform: scale(0.97);
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
  &:last-of-type {
    margin-bottom: 0;
  }
`;

export const ButtonTitle = styled.span`
  margin: 0;
  font-family: ${font.family};
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.011em;
  color: inherit;
`;
